import { SupportTicketStatus } from '@repo/database/browser';
import { findMapValue } from '@repo/utils';
import type {
  FetchSupportTicketsParams,
  SupportTicketStats,
} from '@/api/support-tickets';
import {
  type ApiParamReaders,
  pickApiParams,
  SEARCH_AND_SORT_PARAMS,
  valueParam,
} from '@/lib/helpers/list-api-params';
import type { TabCounts, TranslatableDomainTab } from '@/lib/helpers/list-tabs';
import type { SupportTicketsSearch } from '@/routes/-shared/supportTicketsSearchSchema';

export const SUPPORT_TICKETS_DEFAULT_TAB = 'open';

/**
 * Tab della coda: gli stati "vivi" per primi, poi quelli conclusi e infine
 * tutti. Il contatore (Previxio DS · Ticket) solo sui tre stati che sono
 * lavoro da fare: Aperti `info`, In lavorazione `neutral`, In attesa
 * `warning`; Risolti, Chiusi e Tutti non lo mostrano.
 */
export const SUPPORT_TICKETS_TABS: readonly TranslatableDomainTab<SupportTicketStatus>[] =
  [
    {
      value: 'open',
      text: 'support_tickets.tabs.open',
      filter: SupportTicketStatus.OPEN,
      countTone: 'info',
    },
    {
      value: 'in_progress',
      text: 'support_tickets.tabs.in_progress',
      filter: SupportTicketStatus.IN_PROGRESS,
      countTone: 'neutral',
    },
    {
      value: 'waiting_customer',
      text: 'support_tickets.tabs.waiting_customer',
      filter: SupportTicketStatus.WAITING_CUSTOMER,
      countTone: 'warning',
    },
    {
      value: 'resolved',
      text: 'support_tickets.tabs.resolved',
      filter: SupportTicketStatus.RESOLVED,
    },
    {
      value: 'closed',
      text: 'support_tickets.tabs.closed',
      filter: SupportTicketStatus.CLOSED,
    },
    { value: 'all', text: 'support_tickets.tabs.all' },
  ];

/**
 * Contatori dei tab dai conteggi della coda: solo i tre stati attivi. Senza
 * conteggi (in caricamento) nessuna pill, e lo zero non ne ha mai una
 * (`withTabCounts`).
 */
export function toSupportTicketTabCounts(
  stats: SupportTicketStats | undefined
): TabCounts | undefined {
  if (!stats) return undefined;
  return {
    open: stats.open,
    in_progress: stats.inProgress,
    waiting_customer: stats.waitingCustomer,
  };
}

/** Vuoto di ogni tab senza ricerca né filtri. */
const SUPPORT_TICKETS_TAB_EMPTY_TITLE_KEYS: Readonly<Record<string, string>> = {
  open: 'support_tickets.empty.tabs.open',
  in_progress: 'support_tickets.empty.tabs.in_progress',
  waiting_customer: 'support_tickets.empty.tabs.waiting_customer',
  resolved: 'support_tickets.empty.tabs.resolved',
  closed: 'support_tickets.empty.tabs.closed',
  all: 'support_tickets.empty.title',
};

/**
 * Titolo dello stato vuoto della coda: parla di ricerca o filtri solo se ce
 * n'è uno attivo (anche «Solo miei» / «Non assegnati»), altrimenti dice che
 * il tab è vuoto («Nessun ticket aperto»).
 */
export function getSupportTicketsEmptyTitleKey({
  activeTab,
  hasSearchOrFilters,
}: {
  activeTab: string;
  hasSearchOrFilters: boolean;
}): string {
  if (hasSearchOrFilters) return 'commons.no_results_for_filters';
  return (
    findMapValue(SUPPORT_TICKETS_TAB_EMPTY_TITLE_KEYS, activeTab) ??
    'support_tickets.empty.title'
  );
}

type SupportTicketsApiParams = Omit<FetchSupportTicketsParams, 'skip' | 'take'>;

/** Interruttore che filtra solo acceso: spento = nessun filtro. */
const enabledParam = (value: boolean | null | undefined) =>
  value === true ? true : undefined;

/**
 * Campi di `GET backoffice/support-tickets`; lo stato arriva dal tab (lo
 * `status` dei link vecchi lo traduce lo schema). Gli enum li valida lo
 * schema della rotta; gli interruttori filtrano solo accesi.
 */
const SUPPORT_TICKETS_API_PARAMS: ApiParamReaders<
  SupportTicketsSearch,
  SupportTicketsApiParams
> = {
  ...SEARCH_AND_SORT_PARAMS,
  priority: valueParam,
  category: valueParam,
  type: valueParam,
  tenantId: valueParam,
  assignedToId: valueParam,
  onlyMine: enabledParam,
  unassigned: enabledParam,
  createdFrom: valueParam,
  createdTo: valueParam,
};

export function buildSupportTicketsApiParams(
  search: SupportTicketsSearch,
  status: SupportTicketStatus | undefined
): SupportTicketsApiParams {
  return { ...pickApiParams(search, SUPPORT_TICKETS_API_PARAMS), status };
}
